import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import CustomSelect from "../../../components/CustomSelect";
import ImgUploadInput from "../../../components/ImgUploadInput";
import useCategories from "../../../hooks/useCategories";
import useProviders from "../../../hooks/useProviders";
import clsx from "clsx";
import useAxios from "../../../hooks/useAxios";
import { useFeedBack } from "../../../context/FeedBackContext";
import useServices from "../../../hooks/useServices";
import Toggle from "react-toggle";
import useProducts from "../../../hooks/useProducts";
import { Button, Modal } from "react-bootstrap";
import CustomTable from "../../../components/CustomTable/CustomTable";
import ShortProductsColumns from "../../../components/CustomTable/Columns/ShortProductsColumns";
import useFeatures from "../../../hooks/useFeatures";
import imgUrl from "../../../util/imgUrl";
import ParentProductModal from "../../../components/ParentProductModal";
import CategoriesSelect from "../../../components/CategoriesSelect";
import ServicesSelect from "../../../components/ServicesSelect";

const ProductsUpdate = () => {
    const { id } = useParams();
    const { setLoading, setCustomAlert } = useFeedBack();
    const navigate = useNavigate();

    const [showProductsModal, setShowProductsModal] = useState(false);
    const [initialLoadCompleted, setInitialLoadCompleted] = useState(false);

    // Configuración inicial de filtros
    const initialServicesFilters = { perPage: 200, page: 1 };
    const initialFeaturesFilters = { page: 1, perPage: 100 };
    const initialProductsFilters = { perPage: 10, page: 1, parentsOnly: 'true', exceptId: id };
    const initialCategoriesFilters = { name: '', page: 1, orderBy: 'path,ASC', perPage: 10 };
    const initialSubCategoriesFilters = { page: 1, perPage: 200 };

    const [servicesFilters, setServicesFilters] = useState(initialServicesFilters);
    const [featuresFilters, setFeaturesFilters] = useState(initialFeaturesFilters);
    const [productsFilters, setProductsFilters] = useState(initialProductsFilters);
    const [filters, setFilters] = useState({ name: '', page: 1 });
    const [categoriesFilters, setCategoriesFilters] = useState(initialCategoriesFilters);
    const [subCategoriesFilters, setSubCategoriesFilters] = useState(initialSubCategoriesFilters);
    const [modalSubCategoriesFilters, setModalSubCategoriesFilters] = useState(initialSubCategoriesFilters);

    const [currentCategories, setCurrentCategories] = useState([]);

    const [data, setData] = useState({
        name: '',
        image: null,
        reference: '',
        providerId: '',
        categoryId: '',
        subCategoryId: '',
        dataSheet: '',
        certificate: '',
        certificateExpiryDate: '',
        notifyCertificateExpiryDays: '',
        description: '',
        serviceIds: [],
        code: '',
        sap_code: '',
        provider_code: '',
        price: 0,
        isReplacement: false,
        _method: 'PUT',
        parentId: [],
        parent: '',
        productFeatureOptionIds: [],
    });

    const [imagePreview, setImagePreview] = useState('');
    const [certificatePreview, setCertificatePreview] = useState('');
    const [dataSheetPreview, setDataSheetPreview] = useState('');

    // Hooks con configuración optimizada
    const [{ providers, total, numberOfPages, size, error, loading }, getProviders] = useProviders({
        options: { manual: true, useCache: true }
    });

    const [{ features, total: featuresTotal, numberOfPages: featuresPages, loading: featuresLoading }, getFeatures] = useFeatures({
        options: { manual: true, useCache: true }
    });

    const [{ products, numberOfPages: productsPages, loading: loadingProducts, total: productsTotal }, getProducts] = useProducts({
        options: { manual: true, useCache: true }
    });

    const [{ categories, numberOfPages: categoriesNumberOfPages, loading: loadingCategories }] = useCategories({ params: categoriesFilters }, { useCache: false });

    const [{ categories: subCategories, loading: subCategoriesLoading }, getSubCategories] = useCategories({
        options: { manual: true, useCache: true }
    });

    const [{ categories: modalSubCategories, loading: modalSubCategoriesLoading }, getModalSubCategories] = useCategories({
        options: { manual: true, useCache: true }
    });

    const [{ services, error: servicesError, loading: servicesLoading }, getServices] = useServices({
        axiosConfig: { params: { ...servicesFilters } },
        options: { useCache: true, manual: true }
    });

    const [{ data: product, loading: productLoading }, getProduct] = useAxios({
        url: `/products/${id}`
    }, { useCache: true, manual: true });

    const [{ data: updateData, loading: updateLoading }, updateProduct] = useAxios({
        url: `/products/${id}`,
        method: 'POST'
    }, { manual: true, useCache: false });

    // Carga inicial secuencial
    useEffect(() => {
        const loadInitialData = async () => {
            setLoading?.({ show: true, message: `Cargando información del producto` });

            try {
                // 1. Primero cargar el producto principal
                await getProduct();

                // 2. Luego cargar datos auxiliares
                await Promise.allSettled([
                    getProviders({ params: filters }),
                    getFeatures({ params: featuresFilters }),
                    getServices({ params: servicesFilters })
                ]);

                setInitialLoadCompleted(true);
            } catch (error) {
                console.error('Error loading initial data:', error);
                setCustomAlert?.({
                    title: 'Error',
                    severity: 'error',
                    message: 'Error al cargar los datos del producto',
                    show: true
                });
            } finally {
                setLoading?.({ show: false });
            }
        };

        if (id && !initialLoadCompleted) {
            loadInitialData();
        }
    }, [id, initialLoadCompleted]);

    // Actualizar datos del producto cuando se carga
    useEffect(() => {
        if (product && initialLoadCompleted) {
            const {
                parentId,
                features,
                category,
                subCategory,
                certificate,
                dataSheet,
                createdAt,
                id,
                imagePath,
                provider,
                certificateExpiryDate,
                ...rest
            } = product?.data;

            setData((oldData) => ({
                ...oldData,
                ...rest,
                categoryId: category?.id || '',
                subCategoryId: subCategory?.id || '',
                parentId: parentId ? [parentId] : [],
                productFeatureOptionIds: product?.data?.features?.map(feature => feature?.id) || [],
                certificateExpiryDate: certificateExpiryDate || ''
            }));

            if (provider) {
                setFilters((oldFilters) => ({
                    ...oldFilters,
                    name: provider?.name
                }));
            }

            if (dataSheet) setDataSheetPreview(`${dataSheet}`);
            if (certificate) setCertificatePreview(`${certificate}`);
            if (imagePath) setImagePreview(imgUrl(imagePath));
        }
    }, [product, initialLoadCompleted]);

    // Efectos para cargas dependientes (solo después de carga inicial)
    useEffect(() => {
        if (initialLoadCompleted && data?.categoryId) {
            getSubCategories({
                params: {
                    ...subCategoriesFilters,
                    parentId: data.categoryId
                }
            });
        }
    }, [data?.categoryId, initialLoadCompleted]);

    useEffect(() => {
        if (initialLoadCompleted) {
            getProducts({
                params: productsFilters
            });
        }
    }, [productsFilters, initialLoadCompleted]);

    useEffect(() => {
        if (initialLoadCompleted && productsFilters?.categoryId) {
            setModalSubCategoriesFilters(prev => ({ ...prev, parentId: productsFilters.categoryId }));
            getModalSubCategories({
                params: {
                    ...modalSubCategoriesFilters,
                    parentId: productsFilters.categoryId,
                }
            });
        }
    }, [productsFilters?.categoryId, initialLoadCompleted]);

    // Efectos para estados de loading
    useEffect(() => {
        setLoading?.({
            show: updateLoading,
            message: `Actualizando producto`
        });
    }, [updateLoading]);

    useEffect(() => {
        if (updateData) {
            setCustomAlert?.({
                title: '¡Operación Exitosa!',
                severity: 'success',
                message: `El producto fue actualizado exitosamente.`,
                show: true
            });
            // Recargar datos después de actualizar
            getProduct();
        }
    }, [updateData]);

    useEffect(() => {
        setCurrentCategories((old) => [...old, ...categories]);
    }, [categories])

    useEffect(() => {
        if (initialLoadCompleted) getProviders({ params: filters });
    }, [filters, initialLoadCompleted])

    // Handlers optimizados
    const handleSubmit = useCallback((e) => {
        e.preventDefault?.();

        const formData = new FormData();
        const { parent, productVersions, productFeatureOptionIds, serviceIds, ...rest } = data;

        Object.keys(rest).forEach((key) => {
            if (key !== 'id') {
                if (key === 'isReplacement') {
                    formData.append(key, data[key] ? 1 : 0);
                } else if (data[key]) {
                    if (key === 'image' || key === 'dataSheet' || key === 'certificate') {
                        formData.append(key, data[key], data[key].name);
                    } else if (key === 'serviceIds') {
                        data[key].forEach((id, i) => {
                            formData.append(`${key}[${i}]`, id);
                        });
                    } else {
                        formData.append(key, data[key]);
                    }
                }
            }
        });

        serviceIds?.forEach((serviceId, key) => {
            formData.append(`serviceIds[${key}]`, serviceId);
        });

        productFeatureOptionIds?.forEach((featureId, i) => {
            formData?.append(`productFeatureOptionIds[${i}]`, featureId);
        });

        updateProduct({ data: formData });
    }, [data, updateProduct]);

    const handleProvider = useCallback((provider) => {
        setData((oldData) => ({
            ...oldData,
            providerId: provider?.id
        }));

        setFilters((oldFilters) => ({
            ...oldFilters,
            name: provider?.name
        }));
    }, []);

    const handleChange = useCallback((e) => {
        const { name, type, value, checked } = e.target;

        if (type === 'checkbox') {
            const numericValue = Number(value);
            const currentValues = data[name] || [];

            if (currentValues.includes(numericValue)) {
                setData((oldData) => ({
                    ...oldData,
                    [name]: currentValues.filter(n => n !== numericValue)
                }));
            } else {
                setData((oldData) => ({
                    ...oldData,
                    [name]: [...currentValues, numericValue]
                }));
            }
            return;
        }

        setData((oldData) => ({
            ...oldData,
            [name]: type === 'file' ? e.target.files[0] : value
        }));
    }, [data]);

    const handleProduct = useCallback((value) => {
        setData((oldData) => ({
            ...oldData,
            parent: value?.id === oldData?.parentId?.[0] ? '' : value,
            parentId: value?.id === oldData?.parentId?.[0] ? [] : [value?.id]
        }));
    }, []);

    const handlePage = useCallback((page) => {
        setProductsFilters((oldFilters) => ({
            ...oldFilters,
            page: page
        }));
    }, []);

    // Si está cargando inicialmente, mostrar loading
    if (productLoading && !initialLoadCompleted) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Cargando...</span>
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className="text-end">
                <Link to={'/productos'} className="mx-2 my-2 btn btn-primary">
                    Volver al listado
                </Link>
                <Link to={'/productos/crear'} className="mx-2 my-2 btn btn-primary">
                    Crear nuevo
                </Link>
            </div>

            <div className="card" style={{ width: '100%', marginBottom: 200 }}>
                <div className="card-body">
                    <div className="basic-form">
                        <form onSubmit={handleSubmit}>
                            <div className="row mb-5">
                                <div className="col-md-6 mb-4">
                                    <h5>¿Es un Repuesto?</h5>
                                    <Toggle
                                        onChange={() => {
                                            setData((oldData) => {
                                                return { ...oldData, isReplacement: !oldData?.isReplacement }
                                            })
                                        }}
                                        checked={data?.isReplacement}
                                    />
                                </div>

                                <div className="col-md-6 mb-4">
                                    <ParentProductModal
                                        data={data}
                                        setData={setData}
                                        name="parentId"
                                        defaultValue={product?.data?.parent}
                                    />
                                </div>
                                <div className="form-group mb-3 col-md-8">
                                    <div className="mb-4">
                                        <label>
                                            <h5>
                                                Nombre del Producto
                                            </h5>
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Nombre"
                                            name="name"
                                            autoFocus
                                            value={data?.name}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div className="mb-4">
                                        <label>
                                            Código
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Código"
                                            name="code"
                                            value={data?.code}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div className="mb-4">
                                        <label>
                                            Código SAP
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Código SAP"
                                            name="sap_code"
                                            value={data?.sap_code}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div className="mb-4">
                                        <label>
                                            Código Proveedor
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Código Proveedor"
                                            name="provider_code"
                                            value={data?.provider_code}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div className="mb-4">
                                        <label>
                                            Número de parte
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Número de parte"
                                            name="reference"
                                            value={data?.reference}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div className="mb-4">
                                        <label>
                                            Proveedor
                                        </label>
                                        <CustomSelect
                                            options={providers}
                                            optionLabel="name"
                                            inputPlaceholder="Escribe el nombre..."
                                            isLoading={loading}
                                            onSelectValue={handleProvider}
                                            handleInputChange={(e) => {
                                                setFilters((oldFilters) => {
                                                    return { ...oldFilters, name: e.target.value }
                                                })
                                            }}
                                            inputValue={filters?.name}
                                        />
                                    </div>
                                </div>
                                <div className="form-group mb-3 col-md-4">
                                    <label>
                                        <h5>
                                            Imagen Base del Producto
                                        </h5>
                                    </label>
                                    <ImgUploadInput
                                        previewImage={imagePreview}
                                        style={{ width: '65%' }}
                                        description="imagen del producto"
                                        name="image"
                                        change={handleChange}
                                    />
                                    <div className="text-center mt-4 row">
                                        <div className="col-md-6">
                                            <a
                                                href={dataSheetPreview || "javascript:void(0)"}
                                                target="_blank"
                                                style={{ margin: 0 }}
                                            >
                                                Mostrar Ficha Técnica
                                            </a>
                                            <label
                                                className={clsx(['btn'], {
                                                    "btn-primary": !dataSheetPreview && !data?.dataSheet,
                                                    "btn-success": dataSheetPreview || data?.dataSheet,
                                                })}
                                                htmlFor="datasheet-input"
                                            >
                                                Ficha técnica
                                            </label>
                                            <input
                                                type="file"
                                                hidden
                                                name="dataSheet"
                                                onChange={handleChange}
                                                id="datasheet-input"
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <a
                                                href={certificatePreview || "javascript:void(0)"}
                                                target="_blank"
                                                style={{ margin: 0 }}
                                            >
                                                Mostrar Certificado
                                            </a>
                                            <label
                                                className={clsx(['btn'], {
                                                    "btn-primary": !certificatePreview && !data?.certificate,
                                                    "btn-success": certificatePreview || data?.certificate,
                                                })}
                                                htmlFor="certificate-input"
                                            >
                                                Certificado
                                            </label>
                                            <input
                                                type="file"
                                                hidden
                                                name="certificate"
                                                onChange={handleChange}
                                                id="certificate-input"
                                            />
                                        </div>
                                    </div>
                                </div>
                                {
                                    certificatePreview || data?.certificate ?
                                        <>
                                            <div className="col-md-6 mb-5 form-group animate__animated animate__fadeInLeft">
                                                <label htmlFor="certificate-expiry-input">
                                                    Fecha de expiración del certificado
                                                </label>
                                                <input
                                                    className="form-control"
                                                    type="date"
                                                    value={data?.certificateExpiryDate}
                                                    name="certificateExpiryDate"
                                                    onChange={handleChange}
                                                    id="certificate-expiry-input"
                                                />
                                            </div>
                                            <div className="col-md-6 mb-5 form-group animate__animated animate__fadeInLeft">
                                                <label htmlFor="certificate-expiry-days-input">
                                                    Días para notificar
                                                </label>
                                                <input
                                                    className="form-control"
                                                    type="number"
                                                    value={data?.notifyCertificateExpiryDays}
                                                    name="notifyCertificateExpiryDays"
                                                    onChange={handleChange}
                                                    id="certificate-expiry-days-input"
                                                />
                                            </div>
                                        </>
                                        :
                                        null
                                }
                                <CategoriesSelect
                                    data={data}
                                    name="categoryId"
                                    setData={setData}
                                    defaultValue={product?.data?.category}
                                />
                                <div className="col-md-6 d-none">
                                    <div>
                                        <label>
                                            Categoría
                                        </label>
                                        <select
                                            className="form-control"
                                            disabled={loadingCategories}
                                            name="categoryId"
                                            value={data?.categoryId}
                                            onChange={handleChange}
                                        >
                                            <option value="">
                                                Seleccione una categoría
                                            </option>
                                            {
                                                categories?.map?.((category, i) => {
                                                    return <option key={i} value={category?.id}>{category?.name}</option>
                                                })
                                            }
                                        </select>
                                    </div>
                                </div>
                                <div className="col-md-6 d-none">
                                    <div>
                                        <label>
                                            Sub-Categoría
                                        </label>
                                        <select
                                            className="form-control"
                                            disabled={subCategoriesLoading || !data?.categoryId}
                                            name="subCategoryId"
                                            value={data?.subCategoryId}
                                            onChange={handleChange}
                                        >
                                            <option value="">
                                                Seleccione una sub categoría
                                            </option>
                                            {
                                                subCategories?.map?.((category, i) => {
                                                    return <option key={i} value={category?.id}>{category?.name}</option>
                                                })
                                            }
                                        </select>
                                    </div>
                                </div>
                                <div className="col-md-12 mt-4">
                                    <div>
                                        <label>
                                            Precio
                                        </label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            placeholder="Precio del producto"
                                            name="price"
                                            value={data?.price}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-12 mt-4">
                                    <label>
                                        Descripción
                                    </label>
                                    <textarea
                                        name="description"
                                        onChange={handleChange}
                                        value={data?.description}
                                        className="form-control"
                                        style={{ height: 120 }}
                                        rows={8}
                                    ></textarea>
                                </div>
                                <div className="mt-4">
                                    <h2>
                                        Características
                                    </h2>
                                    <div className="row">
                                        {
                                            features?.length > 0 ?
                                                features?.map((feature, i) => {
                                                    return (
                                                        <div className="col-md-3" key={i}>
                                                            <h5>
                                                                {feature?.name}
                                                            </h5>
                                                            <div
                                                                className="custom-scrollbar scrollbar-primary"
                                                                style={{ maxHeight: '150px', overflowY: 'auto' }}
                                                            >
                                                                {
                                                                    feature?.options?.map((option, i) => {
                                                                        return (
                                                                            <div key={i}>
                                                                                <input
                                                                                    value={option?.id}
                                                                                    type="checkbox"
                                                                                    name="productFeatureOptionIds"
                                                                                    style={{ cursor: 'pointer' }}
                                                                                    checked={data?.productFeatureOptionIds?.includes(option?.id)}
                                                                                    onChange={handleChange}
                                                                                    className="mx-2"
                                                                                    id={`feature-options-${option?.id}`}
                                                                                />
                                                                                <label
                                                                                    htmlFor={`feature-options-${option?.id}`}
                                                                                    style={{ cursor: 'pointer' }}
                                                                                >
                                                                                    {option?.name}
                                                                                </label>
                                                                            </div>
                                                                        )
                                                                    })
                                                                }
                                                            </div>
                                                        </div>
                                                    )
                                                })
                                                :
                                                <div className="text-center">
                                                    No hay Características.
                                                </div>
                                        }
                                    </div>
                                </div>
                                <ServicesSelect
                                    data={data}
                                    name="serviceIds"
                                    setData={setData}
                                    multiple
                                />
                            </div>

                            <div className="mb-3 d-flex justify-content-end">
                                <button
                                    type="button"
                                    onClick={() => { navigate(-1) }}
                                    className="btn btn-danger mx-2"
                                >
                                    Volver
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary mx-2"
                                    disabled={updateLoading}
                                >
                                    {updateLoading ? 'Actualizando...' : 'Actualizar'}
                                </button>
                            </div>
                        </form>
                        <br />
                        <div style={{ borderTop: '1px solid gray', padding: '15px 0px' }}>
                            <div className="row">
                                <div className="col-md-6">
                                    <h3>Versiones</h3>
                                </div>
                                <div className="col-md-6 text-end">
                                    <a
                                        style={{ marginLeft: 'auto' }}
                                        className="btn btn-primary"
                                        href={`/productos/crear?parentId=${product?.data?.id}`}
                                    >
                                        Añadir versión
                                    </a>
                                </div>
                            </div>
                            {
                                product?.data?.childrens?.length > 0 ?
                                    product?.data?.childrens?.map((productChild, i) => {
                                        return (
                                            <div className="row align-items-center" key={i}>
                                                <div className="col-md-2">
                                                    <img
                                                        style={{ height: '80px', width: '80px', borderRadius: '10px' }}
                                                        src={imgUrl(productChild?.imagePath)}
                                                        alt=""
                                                    />
                                                </div>
                                                <div className="col-md-3">
                                                    <a href={`/productos/${productChild?.id}?name="${productChild?.name}"`}>
                                                        <h5>
                                                            {productChild?.name}
                                                        </h5>
                                                    </a>
                                                </div>
                                                <div className="col-md-3">
                                                    <h5>
                                                        {productChild?.code}
                                                    </h5>
                                                </div>
                                                <div className="col-md-2">
                                                    <h5>
                                                        {productChild?.price}$
                                                    </h5>
                                                </div>
                                                <div className="col-md-2">
                                                    <a
                                                        className="btn btn-block btn-warning"
                                                        href={`/productos/${productChild?.id}?name="${productChild?.name}"`}
                                                    >
                                                        Editar
                                                    </a>
                                                    <button type="button" className="btn btn-block btn-danger mt-2">
                                                        Remover
                                                    </button>
                                                </div>
                                            </div>
                                        )
                                    })
                                    :
                                    <div className="text-center text-danger">
                                        <h4>
                                            No tiene versiones
                                        </h4>
                                    </div>
                            }
                        </div>
                    </div>
                </div>
            </div>
            <Modal show={showProductsModal} className="fade" size="xl" onHide={() => setShowProductsModal(false)}>
                <Modal.Header>
                    <Modal.Title>Productos</Modal.Title>
                    <Button
                        variant=""
                        className="btn-close"
                        onClick={() => setShowProductsModal(false)}
                    >
                    </Button>
                </Modal.Header>
                <Modal.Body>
                    <div className="row">
                        <div className="col-md-4">
                            <div>
                                <label style={{ marginRight: '10px' }}>Nombre:</label>
                                <input
                                    placeholder="Escriba el nombre..."
                                    className="form-control"
                                    value={productsFilters?.name}
                                    type="text"
                                    onChange={(e) => {
                                        setProductsFilters((oldFilters) => {
                                            return {
                                                ...oldFilters,
                                                name: e.target.value,
                                                page: 1
                                            }
                                        })
                                    }}
                                />
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div>
                                <label>
                                    Categoría
                                </label>
                                <select
                                    className="form-control"
                                    disabled={loadingCategories}
                                    name="categoryId"
                                    value={productsFilters?.categoryId}
                                    onChange={(e) => {
                                        setProductsFilters((oldFilters) => {
                                            return {
                                                ...oldFilters,
                                                [e.target.name]: e.target.value,
                                                page: 1
                                            }
                                        })
                                    }}
                                >
                                    <option value="">
                                        Seleccione una categoría
                                    </option>
                                    {
                                        categories?.map?.((category, i) => {
                                            return <option key={i} value={category?.id}>{category?.name}</option>
                                        })
                                    }
                                </select>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div>
                                <label>
                                    Sub-Categoría
                                </label>
                                <select
                                    className="form-control"
                                    disabled={modalSubCategoriesLoading || !productsFilters?.categoryId}
                                    name="subCategoryId"
                                    value={productsFilters?.subCategoryId}
                                    onChange={(e) => {
                                        setProductsFilters((oldFilters) => {
                                            return {
                                                ...oldFilters,
                                                [e.target.name]: e.target.value,
                                                page: 1
                                            }
                                        })
                                    }}
                                >
                                    <option value="">
                                        Seleccione una sub categoría
                                    </option>
                                    {
                                        modalSubCategories?.map?.((category, i) => {
                                            return <option key={i} value={category?.id}>{category?.name}</option>
                                        })
                                    }
                                </select>
                            </div>
                        </div>
                    </div>
                    <CustomTable
                        onSelectValue={handleProduct}
                        loading={loadingProducts}
                        withoutGlobalActions
                        variant="simple"
                        hideSelectAll
                        selectedValues={data?.parentId}
                        total={productsTotal}
                        values={products}
                        currentPage={productsFilters.page}
                        collumns={ShortProductsColumns}
                        changePage={handlePage}
                        pages={productsPages}
                        perPage={productsFilters?.perPage}
                        onPerPageChange={(e) => setProductsFilters((oldFilters) => {
                            return {
                                ...oldFilters,
                                [e.target.name]: e.target.value,
                                page: 1
                            }
                        })}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <button onClick={() => { setShowProductsModal(false) }} className="btn btn-danger">
                        Cerrar
                    </button>
                </Modal.Footer>
            </Modal>
        </div>
    )
}

export default ProductsUpdate;